import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import moment from 'moment';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectPhase } from 'api/project';
import { Button } from 'components/atoms/button';
import { DateValue } from 'components/atoms/due-date-picker';
import { humanise } from 'components/atoms/humanised-date';
import { TaskTemplateLight } from 'data/templates/tasks';
import { useTranslate } from 'hooks/use-translation';
import {
  ButtonGroup,
  CurrentStatus,
  FooterText,
  NewStatus,
  PhaseDurationStatus,
  SpacedButton,
} from './styles';

export enum DrawerPhase {
  PhaseOne = 1,
  PhaseTwo,
}

export interface CheckBoxValue {
  template: TaskTemplateLight;
  checked: boolean;
}

interface DrawerFooterProps {
  isTaskTemplatesLoaded: boolean;
  drawerPhase: number;
  moveToPhase: (phase: number) => void;
  checkBoxValues: CheckBoxValue[];
  setCheckBoxValues: React.Dispatch<React.SetStateAction<CheckBoxValue[]>>;
  onClose: () => void;
  onClickNext: () => void;
  noneSelected: boolean;
  selectedTemplates: TaskTemplateLight[];
  isAddInProgress: boolean;
  projectPhase?: ProjectPhase;
  initialPhaseDates: (DateValue | undefined)[];
  modifiedPhaseDates: (DateValue | undefined)[];
}

export const DrawerFooter: FC<DrawerFooterProps> = ({
  isTaskTemplatesLoaded,
  drawerPhase,
  moveToPhase,
  checkBoxValues,
  setCheckBoxValues,
  onClose,
  onClickNext,
  noneSelected,
  selectedTemplates,
  isAddInProgress,
  initialPhaseDates,
  modifiedPhaseDates,
}) => {
  const translate = useTranslate();
  const [startDate, dueDate] = initialPhaseDates;
  const [modifiedStartDate, modifiedDueDate] = modifiedPhaseDates;

  const [isPhaseExtended, setIsPhaseExtended] = useState(false);

  const currentPhaseDuration = translate(
    'projects.task_template.drawer.footer.current.phase_duration',
  );
  const nextPhaseDuration = translate(
    'projects.task_template.drawer.footer.new.phase_duration',
  );

  useEffect(() => {
    if (
      moment(modifiedStartDate).isBefore(moment(startDate)) ||
      moment(modifiedDueDate).isAfter(moment(dueDate))
    ) {
      setIsPhaseExtended(true);
    } else {
      setIsPhaseExtended(false);
    }
  }, [dueDate, modifiedDueDate, modifiedStartDate, startDate]);

  return (
    <>
      <FooterText>
        {drawerPhase === DrawerPhase.PhaseOne ? (
          <FormattedMessage id="projects.task_template.drawer.footer.text" />
        ) : (
          <PhaseDurationStatus>
            <CurrentStatus>
              {`${currentPhaseDuration} ${humanise(
                moment(startDate),
              )} - ${humanise(moment(dueDate))}`}
            </CurrentStatus>
            <NewStatus showWarning={isPhaseExtended}>
              {`${nextPhaseDuration} ${humanise(
                moment(modifiedStartDate),
              )} - ${humanise(moment(modifiedDueDate))}`}
            </NewStatus>
          </PhaseDurationStatus>
        )}
      </FooterText>
      <ButtonGroup>
        <Button
          onClick={
            isTaskTemplatesLoaded
              ? () => {
                  if (drawerPhase === DrawerPhase.PhaseTwo) {
                    moveToPhase(DrawerPhase.PhaseOne);
                  } else {
                    setCheckBoxValues(
                      checkBoxValues.map((t) => ({
                        ...t,
                        checked: false,
                      })),
                    );
                  }
                }
              : () => onClose()
          }
        >
          <FormattedMessage
            id={
              isTaskTemplatesLoaded
                ? drawerPhase === DrawerPhase.PhaseOne
                  ? 'clear'
                  : 'back'
                : 'cancel'
            }
          />
        </Button>
        <SpacedButton
          onClick={onClickNext}
          type="primary"
          disabled={!isTaskTemplatesLoaded || noneSelected}
          loading={isAddInProgress}
        >
          {drawerPhase === DrawerPhase.PhaseOne
            ? translate('projects.task_template.drawer.next.step')
            : translate('projects.task_template.drawer.add_tasks', {
                count: selectedTemplates.length,
              })}
          <ArrowRight16 />
        </SpacedButton>
      </ButtonGroup>
    </>
  );
};
